import { clsx } from "clsx";
import { type FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMatch } from "react-router";
import { ActionButtonIntent, PanelGroup, PanelOrientation, PanelPosition } from "widgets";

import { capitalizeFirstLetter } from "../../app-utils/string.js";
import { type InputUserGroupInput, type ListingMapEntry, type OrganismsInfo, type UserGroup } from "../../graphql.js";
import { getProjectLabel } from "../../redux/modules/config.js";
import { createContentBrowserTableSpec } from "../../redux/modules/content-browser.js";
import { getTableDataKey, type ITableInitializationOptions } from "../../redux/modules/table-data.js";
import {
  changeGroupByAttributeFilter,
  deleteSingleUserGroup,
  editSingleUserGroup,
  type IUserGroupAdminState,
  loadUserGroupAdminListing,
  loadUserGroups,
  VISIBLE_ORGANISM_TABLE_FIELDS_BY_ATTRIBUTE,
  VISIBLE_SAMPLE_TYPE_TABLE_FIELDS_BY_ATTRIBUTE,
  VISIBLE_USER_TABLE_FIELDS_BY_ATTRIBUTE,
} from "../../redux/modules/user-group-admin.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import {
  CONTENT_BROWSER_FEATURE_TYPE_ORGANISMS,
  CONTENT_BROWSER_FEATURE_TYPE_SAMPLE_TYPES,
  CONTENT_BROWSER_FEATURE_TYPE_USERS,
  GROUP_BY_ATTRIBUTE_USER_GROUP,
  ORGANISM_TABLE_COLUMNS,
  SAMPLE_TYPE_TABLE_COLUMNS,
  USER_TABLE_COLUMNS,
} from "../../settings/settings.js";
import ContentBrowserChartView from "../content-browser/ContentBrowserChart.js";
import ContentBrowserTableView from "../content-browser/ContentBrowserTableView.js";
import {
  UserGroupAdminButtonHeader,
  UserGroupAdminTableButtonHeader,
} from "../core/navigation-panel/ButtonHeaderHoc.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";
import { ConfirmationDialog } from "../core/overlays/Dialogs.js";
import { ModalControlledOverlay } from "../core/overlays/index.js";
import styleLayout from "../core/styles/PageLayout.module.scss";
import EditUserGroupDialog from "./EditUserGroupDialog.js";
import { USER_GROUP_ADMIN_FEATURES } from "./ProjectGroupsAdminPage.js";

/**
 * User Group Admin Page main component
 */
const UserGroupAdminPage: FC = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const match = useMatch("/admin/projectGroups/:featureType/:groupByAttribute/:entityType");
  const { content, isLoadingListing, selectedEntry, userGroups } = useSelector<IAppState, IUserGroupAdminState>(
    (state) => state.userGroupAdmin,
  );

  const isLoadingFeaturesTable = useSelector<IAppState, boolean>((state) => {
    if (match?.params.featureType) {
      const featureTableSpec = createContentBrowserTableSpec(match?.params.featureType);
      const tableData = state.tableData.tables[getTableDataKey(featureTableSpec)];
      return tableData && tableData.isLoadingTableMeta;
    } else {
      return false;
    }
  });

  const selectedUserGroupAdminFeature = useMemo(
    () => USER_GROUP_ADMIN_FEATURES.find((navItem) => navItem.eventKey === match?.params.featureType),
    [match?.params.featureType],
  );
  const selectedContentBrowserAttribute = useMemo(
    () =>
      selectedUserGroupAdminFeature?.navItems.find((subroute) => subroute.eventKey === match?.params.groupByAttribute),
    [selectedUserGroupAdminFeature, match?.params.groupByAttribute],
  );

  const [userGroupToEdit, setUserGroupToEdit] = useState<UserGroup>();
  const [userGroupToDelete, setUserGroupToDelete] = useState<UserGroup>();

  useEffect(() => {
    // First load
    if (match?.params.featureType && match?.params.groupByAttribute) {
      void dispatch(
        loadUserGroupAdminListing(selectedUserGroupAdminFeature.eventKey, selectedContentBrowserAttribute.eventKey),
      );
      void dispatch(loadUserGroups());
    }
  }, []);

  useEffect(() => {
    if (match?.params.featureType && match?.params.groupByAttribute && !isLoadingListing) {
      if (selectedEntry) {
        dispatch(changeGroupByAttributeFilter(null)); // reset selected entry upon menu change
      }
      void dispatch(
        loadUserGroupAdminListing(selectedUserGroupAdminFeature.eventKey, selectedContentBrowserAttribute.eventKey),
      );
    }
  }, [match?.params.featureType, match?.params.groupByAttribute]);

  function findUserGroupFromAttributeEntry(entry: ListingMapEntry) {
    return userGroups.find((userGroup) => userGroup.id === Number.parseInt(entry.key, 10));
  }

  function closeDialogs() {
    setUserGroupToEdit(undefined);
    setUserGroupToDelete(undefined);
  }

  // return a string for disabled tooltip: will drive the disabled state !
  function editUserGroupDisabledTooltip(entry: ListingMapEntry): string {
    if (entry.key === "0") {
      return "The 'public' user group cannot be modified.";
    }
    const userGroup: UserGroup = findUserGroupFromAttributeEntry(entry);
    if (!userGroup) {
      return "This user group cannot be edited.";
    }

    return null;
  }

  // return a string for disabled tooltip: will drive the disabled state !
  function deleteUserGroupDisabledTooltip(entry: ListingMapEntry): string {
    if (entry.key === "0") {
      return "The 'public' user group cannot be deleted.";
    }
    const userGroup: UserGroup = findUserGroupFromAttributeEntry(entry);
    if (!userGroup) {
      return "This user group cannot be deleted.";
    } else if (userGroup.projects.length > 0) {
      return `This user group is associated with ${userGroup.projects.length} ${getProjectLabel(
        false,
        false,
      )}(s) e.g. '${userGroup.projects[0].name}'`;
    }

    return null;
  }

  function getBarTooltip(entry: ListingMapEntry) {
    // only applies when entry corresponds to a User Group
    const userGroup = findUserGroupFromAttributeEntry(entry);
    return userGroup?.description;
  }

  const getTableInitializationOptions = (): ITableInitializationOptions => {
    let visibleFields: string[];
    let sortExpression: string;
    switch (selectedUserGroupAdminFeature.eventKey) {
      case CONTENT_BROWSER_FEATURE_TYPE_ORGANISMS:
        sortExpression = ORGANISM_TABLE_COLUMNS.NAME;
        visibleFields = VISIBLE_ORGANISM_TABLE_FIELDS_BY_ATTRIBUTE[selectedContentBrowserAttribute.eventKey];
        break;
      case CONTENT_BROWSER_FEATURE_TYPE_USERS:
        sortExpression = USER_TABLE_COLUMNS.USERNAME;
        visibleFields = VISIBLE_USER_TABLE_FIELDS_BY_ATTRIBUTE[selectedContentBrowserAttribute.eventKey];
        break;
      case CONTENT_BROWSER_FEATURE_TYPE_SAMPLE_TYPES:
        sortExpression = SAMPLE_TYPE_TABLE_COLUMNS.NAME;
        visibleFields = VISIBLE_SAMPLE_TYPE_TABLE_FIELDS_BY_ATTRIBUTE[selectedContentBrowserAttribute.eventKey];
        break;
    }

    return {
      visibleColumns: visibleFields,
      sortExpression: sortExpression,
    };
  };

  const featureContent = content?.[selectedUserGroupAdminFeature.eventKey];
  const groupByContent = featureContent?.[selectedContentBrowserAttribute.eventKey];

  const listing: ListingMapEntry[] = featureContent && groupByContent?.listing;
  const organismsInfo: OrganismsInfo = featureContent && groupByContent?.info;

  // special behaviour for User Groups (can be deleted and edited)
  const userGroupAttribute: boolean = selectedContentBrowserAttribute?.eventKey === GROUP_BY_ATTRIBUTE_USER_GROUP;

  return (
    <>
      <PageHeader
        title={capitalizeFirstLetter(selectedUserGroupAdminFeature?.label)}
        subtitle={capitalizeFirstLetter(selectedContentBrowserAttribute?.label) + " Overview"}
        badge={String(featureContent?.count ?? 0)}
        actionWidgets={[
          <UserGroupAdminButtonHeader key={"content-browser-table-view-action-1"} />,
          match?.params.entityType && (
            <UserGroupAdminTableButtonHeader
              key={"content-browser-table-view-action-3"}
              entityType={match?.params.entityType}
            />
          ),
        ]}
      />
      <div className={clsx(styleLayout.page, "content-browser")}>
        {!!userGroupToEdit && (
          <ModalControlledOverlay close={closeDialogs} isOpen={true}>
            {({ close }) => (
              <EditUserGroupDialog
                userGroup={userGroupToEdit}
                onEditUserGroup={(payload: InputUserGroupInput) => dispatch(editSingleUserGroup(payload))}
                onApply={close}
                onCancel={close}
              />
            )}
          </ModalControlledOverlay>
        )}

        {!!userGroupToDelete && (
          <ModalControlledOverlay close={closeDialogs} isOpen={true}>
            {({ close }) => (
              <ConfirmationDialog
                headerProps={{
                  title: "Delete User Group",
                }}
                mainActionButtonProps={{
                  label: `Delete`,
                  onPress: () => dispatch(deleteSingleUserGroup(userGroupToDelete)).then(() => close()),
                  intent: ActionButtonIntent.Danger,
                }}
                closeFn={close}
              >
                Are you sure you want to delete the user group &apos;{userGroupToDelete.name}&apos; ?
              </ConfirmationDialog>
            )}
          </ModalControlledOverlay>
        )}

        <PanelGroup
          defaultSize={252}
          minSize={200}
          className={"content-browser"}
          isContinuous
          fixedPanel={PanelPosition.Leading}
          orientation={PanelOrientation.Vertical}
        >
          <div className={clsx(styleLayout.grid_pane)}>
            <ContentBrowserChartView
              key="user-group-admin-chart-view"
              isLoadingListing={isLoadingFeaturesTable || isLoadingListing}
              listing={listing}
              organismsInfo={organismsInfo}
              selectedEntry={selectedEntry}
              selectedContentBrowserFeature={selectedUserGroupAdminFeature}
              selectedContentBrowserAttribute={selectedContentBrowserAttribute}
              userGroupAdminContext={true}
              changeGroupByAttributeFilter={(entry) => dispatch(changeGroupByAttributeFilter(entry))}
              getBarTooltip={userGroupAttribute && getBarTooltip}
              handleEditAttribute={
                userGroupAttribute
                  ? (entry) => {
                      setUserGroupToEdit((previous) =>
                        entry ? (findUserGroupFromAttributeEntry(entry) ?? previous) : previous,
                      );
                    }
                  : undefined
              }
              handleDeleteAttribute={
                userGroupAttribute
                  ? (entry) => {
                      setUserGroupToDelete((previous) =>
                        entry ? (findUserGroupFromAttributeEntry(entry) ?? previous) : previous,
                      );
                    }
                  : undefined
              }
              editAttributeDisabledTooltip={editUserGroupDisabledTooltip}
              deleteAttributeDisabledTooltip={deleteUserGroupDisabledTooltip}
              totalCount={featureContent?.count}
            />
          </div>
          <div className={clsx(styleLayout.grid_pane)}>
            <ContentBrowserTableView
              selectedContentBrowserFeature={selectedUserGroupAdminFeature}
              selectedContentBrowserAttribute={selectedContentBrowserAttribute}
              selectedEntityType={match?.params.entityType}
              getTableInitializationOptions={getTableInitializationOptions}
              userGroupAdminContext={true}
            />
          </div>
        </PanelGroup>
      </div>
    </>
  );
};

export default UserGroupAdminPage;
